<template>
    <div v-loading="m__loading">
        <div class="flex space-x-2 items-center mb-4">
            <p class="text-[0.9rem] font-semibold">
                {{ $t('common.detailed_analysis') }}
            </p>

            <div class="!ml-auto"></div>

            <el-select
                v-model="selected_dimension"
                class="w-[200px]"
                size="small"
            >
                <el-option
                    v-for="item in dimension_options"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                >
                </el-option>
            </el-select>

            <el-button size="small" @click="show_custom_column_dialog = true">
                {{ $t('button.custom_column') }}
            </el-button>

            <div
                class="cursor-pointer p-2 rounded-lg bg-bg"
                @click="handleExportExcel"
            >
                <el-tooltip
                    class="item"
                    :content="$t(`common.export_excel`)"
                    effect="dark"
                    placement="top"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="w-4 h-4"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                        />
                    </svg>
                </el-tooltip>
            </div>
        </div>

        <div>
            <el-table
                class="campaign-table"
                :max-height="400"
                :min-height="100"
                :data="detail_data"
                @sort-change="handleSortChange"
            >
                <el-table-column
                    width="160"
                    :show-overflow-tooltip="true"
                    :label="formatBySelectedDimension('label')"
                    :fixed="true"
                >
                    <template #default="scope">
                        <p>
                            {{ formatBySelectedDimension('name', scope.row) }}
                        </p>
                    </template>
                </el-table-column>

                <el-table-column
                    v-for="(item, index) in selected_metrics_for_table"
                    :key="index"
                    width="160"
                    :show-overflow-tooltip="true"
                    :prop="item"
                    :label="$t(`page.campaign.column_${item}`)"
                    sortable="custom"
                    align="right"
                >
                    <template v-slot:header>
                        <el-tooltip
                            class="item"
                            :content="$t(`page.campaign.column_${item}`)"
                            effect="dark"
                            placement="top"
                        >
                            <div slot="content" class="max-w-[360px]">
                                <p
                                    class="text-[0.85rem]"
                                    v-html="$t(`page.campaign.column_${item}`)"
                                ></p>

                                <p
                                    v-if="
                                        $t(
                                            `page.campaign.description_column_${item}`
                                        ) !==
                                        `page.campaign.description_column_${item}`
                                    "
                                    class="mt-1"
                                    v-html="
                                        $t(
                                            `page.campaign.description_column_${item}`
                                        )
                                    "
                                ></p>
                            </div>

                            <p>
                                {{ $t(`page.campaign.column_${item}`) }}
                            </p>
                        </el-tooltip>
                    </template>

                    <template #default="scope">
                        <p>
                            {{
                                m__formatterColumn(
                                    scope.row[item],
                                    item,
                                    currency
                                )
                            }}
                        </p>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <CustomColumnDialog
            :visible.sync="show_custom_column_dialog"
            :column_values.sync="selected_metrics_for_table"
        ></CustomColumnDialog>
    </div>
</template>

<script>
import {
    getReportFromTTA,
    exportExcel
} from '@/services/atosa-tiktok-ads/reporting'
import campaignMixin from '@/views/campaign/_mixin'
import {
    getQueryTool,
    getAllDeviceModels
} from '@/services/atosa-tiktok-ads/tool'
import { formatAge } from '@/views/campaign/_components/view-data-drawer/_utils'
import CustomColumnDialog from '@/views/campaign/_components/view-data-drawer/_components/custom-column-dialog'
import moment from 'moment'

export default {
    components: {
        CustomColumnDialog
    },

    mixins: [campaignMixin],

    props: [
        'tiktok_account_id',
        'advertiser_id',
        'ranger_date',
        'currency',
        'objective_type',
        'metrics',
        'location_options',
        'device_model_options',
        'interest_category_options',
        'type',
        'data_id'
    ],

    data() {
        return {
            selected_metrics_for_table: [],
            show_custom_column_dialog: false,
            dimension_options: [
                {
                    value: 'age',
                    label: this.$t('common.age')
                },
                {
                    value: 'gender',
                    label: this.$t('common.gender')
                },
                {
                    value: 'age,gender',
                    label: this.$t('common.age_and_gender')
                },
                {
                    value: 'country_code',
                    label: this.$t('common.country')
                },
                {
                    value: 'province_id',
                    label: this.$t('common.province')
                },
                {
                    value: 'ac',
                    label: this.$t('common.network')
                },
                {
                    value: 'interest_category',
                    label: this.$t('page.campaign.interests')
                },
                // {
                //     value: 'behavior_id',
                //     label: this.$t('common.behavior')
                // },
                {
                    value: 'platform',
                    label: this.$t('common.operating_system')
                },
                {
                    value: 'device_brand_id',
                    label: this.$t('common.device')
                }
            ],
            selected_dimension: null,
            detail_data: [],
            order_field: 'spend',
            order_type: 'DESC'
        }
    },

    computed: {
        location_options_inline: {
            get() {
                return this.location_options
            },
            set(value) {
                this.$emit('update:location_options', value)
            }
        },

        device_model_options_inline_inline: {
            get() {
                return this.device_model_options
            },
            set(value) {
                this.$emit('update:device_model_options', value)
            }
        },

        interest_category_options_inline: {
            get() {
                return this.interest_category_options
            },
            set(value) {
                this.$emit('update:interest_category_options', value)
            }
        }
    },

    watch: {
        ranger_date() {
            this.fetchOverviewReport(true)
        },

        data_id() {
            this.fetchOverviewReport(true)
        },

        selected_dimension() {
            this.fetchOverviewReport(true)
        }
    },

    mounted() {
        this.selected_dimension = this.dimension_options[0].value
        this.fetchOverviewReport()

        this.selected_metrics_for_table = this.p__trend_basic_chart_options.map(
            (item) => item.value
        )
    },

    methods: {
        async fetchOverviewReport(reset_sort = false) {
            if (!this.data_id) {
                return
            }

            this.m__loading = true

            try {
                if (reset_sort) {
                    this.order_field = 'spend'
                    this.order_type = 'DESC'
                }

                if (
                    this.selected_dimension === 'province_id' &&
                    this.location_options_inline.length === 0
                ) {
                    const response = await getQueryTool(
                        this.tiktok_account_id,
                        {
                            path: 'region/',
                            params: {
                                advertiser_id: this.advertiser_id,
                                placements: JSON.stringify([
                                    'PLACEMENT_TIKTOK'
                                ]),
                                objective_type:
                                    this.objective_type === 'SHOP_PURCHASES'
                                        ? 'PRODUCT_SALES'
                                        : this.objective_type
                            }
                        }
                    )
                    this.location_options_inline = response.data.region_info
                }

                if (
                    this.selected_dimension === 'device_brand_id' &&
                    this.device_model_options_inline.length === 0
                ) {
                    const response = await getAllDeviceModels()
                    this.device_model_options_inline = response.data
                }

                if (
                    this.selected_dimension === 'interest_category' &&
                    this.interest_category_options_inline.length === 0
                ) {
                    const response = await getQueryTool(
                        this.tiktok_account_id,
                        {
                            path: 'interest_category/',
                            params: {
                                advertiser_id: this.advertiser_id,
                                language: this.$i18n.locale
                            }
                        }
                    )
                    this.interest_category_options_inline =
                        response.data.interest_categories
                }

                let data_level = ''
                let field_name = ''
                if (this.type === 'campaign_id') {
                    data_level = 'AUCTION_CAMPAIGN'
                    field_name = 'campaign_ids'
                } else if (this.type === 'adgroup_id') {
                    data_level = 'AUCTION_ADGROUP'
                    field_name = 'adgroup_ids'
                } else if (this.type === 'ad_id') {
                    data_level = 'AUCTION_AD'
                    field_name = 'ad_ids'
                }

                const filtering = [
                    {
                        filter_value: JSON.stringify([this.data_id]),
                        field_name,
                        filter_type: 'IN'
                    }
                ]

                const response = await getReportFromTTA(
                    this.tiktok_account_id,
                    {
                        page_size: 50,
                        page: 1,
                        filtering: JSON.stringify(filtering),
                        advertiser_id: this.advertiser_id,
                        start_date: this.ranger_date[0],
                        end_date: this.ranger_date[1],
                        type: this.type,
                        report_type: 'AUDIENCE',
                        order_field: this.order_field,
                        order_type: this.order_type,
                        metrics: JSON.stringify(this.metrics),
                        dimensions: JSON.stringify(
                            this.selected_dimension.split(',')
                        ),
                        data_level
                    }
                )
                this.detail_data = response.data.list.map((item) => ({
                    ...item.dimensions,
                    ...item.metrics
                }))
            } catch (error) {
                console.error(error)
            }

            this.m__loading = false
        },

        async handleSortChange({ column, prop, order }) {
            this.order_field = prop
            this.order_type = order === 'descending' ? 'DESC' : 'ASC'

            this.fetchOverviewReport()
        },

        formatBySelectedDimension(type, row = null) {
            if (this.selected_dimension === 'age') {
                if (type === 'label') {
                    return this.$t('common.age')
                }
                return formatAge(row['age'])
            }
            if (this.selected_dimension === 'gender') {
                if (type === 'label') {
                    return this.$t('common.gender')
                }
                return row['gender'] === 'MALE'
                    ? this.$t('common.male')
                    : this.$t('common.female')
            }
            if (this.selected_dimension === 'age,gender') {
                if (type === 'label') {
                    return this.$t('common.age_and_gender')
                }
                return `${formatAge(row['age'])} + ${
                    row['gender'] === 'MALE'
                        ? this.$t('common.male')
                        : this.$t('common.female')
                }`
            }
            if (this.selected_dimension === 'country_code') {
                if (type === 'label') {
                    return this.$t('common.country')
                }
                return row['country_code']
            }
            if (this.selected_dimension === 'province_id') {
                if (type === 'label') {
                    return this.$t('common.province')
                }

                if (this.location_options_inline.length) {
                    const item = this.location_options_inline.find(
                        (item) => item.location_id === row['province_id']
                    )
                    if (item) {
                        return item.name
                    }
                }
                return row['province_id']
            }
            if (this.selected_dimension === 'ac') {
                if (type === 'label') {
                    return this.$t('common.network')
                }
                return row['ac']
            }
            if (this.selected_dimension === 'interest_category') {
                if (type === 'label') {
                    return this.$t('page.campaign.interests')
                }
                if (this.interest_category_options_inline.length) {
                    const item = this.interest_category_options_inline.find(
                        (item) =>
                            item.interest_category_id ===
                            row['interest_category_v2']
                    )
                    if (item) {
                        return item.interest_category_name
                    }
                }
                return row['interest_category_v2']
            }
            if (this.selected_dimension === 'platform') {
                if (type === 'label') {
                    return this.$t('common.operating_system')
                }
                return row['platform']
            }
            if (this.selected_dimension === 'device_brand_id') {
                if (type === 'label') {
                    return this.$t('common.device')
                }
                if (this.device_model_options_inline.length) {
                    const item = this.device_model_options_inline.find(
                        (item) =>
                            item.device_model_id === row['device_brand_id']
                    )
                    if (item) {
                        return item.device_model_name
                    }
                }
                return row['device_brand_id']
            }
            // if (this.selected_dimension === 'behavior_id') {
            //     if (type === 'label') {
            //         return this.$t('common.behavior')
            //     }
            //     return row['behavior_id']
            // }

            return ''
        },

        async handleExportExcel() {
            try {
                let data_level = ''
                let field_name = ''
                if (this.type === 'campaign_id') {
                    data_level = 'AUCTION_CAMPAIGN'
                    field_name = 'campaign_ids'
                } else if (this.type === 'adgroup_id') {
                    data_level = 'AUCTION_ADGROUP'
                    field_name = 'adgroup_ids'
                } else if (this.type === 'ad_id') {
                    data_level = 'AUCTION_AD'
                    field_name = 'ad_ids'
                }

                const filtering = [
                    {
                        filter_value: JSON.stringify([this.data_id]),
                        field_name,
                        filter_type: 'IN'
                    }
                ]
                const response = await exportExcel(this.tiktok_account_id, {
                    page_size: 50,
                    page: 1,
                    filtering: JSON.stringify(filtering),
                    advertiser_id: this.advertiser_id,
                    start_date: this.ranger_date[0],
                    end_date: this.ranger_date[1],
                    type: this.type,
                    report_type: 'AUDIENCE',
                    order_field: this.order_field,
                    order_type: this.order_type,
                    metrics: JSON.stringify(this.metrics),
                    dimensions: JSON.stringify(
                        this.selected_dimension.split(',')
                    ),
                    data_level
                })

                const date = moment().format('HHmmDDMMyyyy')

                const filename = `excel-audience-${date}`

                this.p__exportExcelByBlob(response, filename)
            } catch (error) {
                console.error(error)
            }
        }
    }
}
</script>
